import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import type { DimensionUnit } from '@easymoving/types';
import { kgToLb, lbToKg, M3_TO_FT3 } from '../query/measure';

export type UnitSystem = 'metric' | 'imperial';
const KEY = 'easymoving.units';

const listeners = new Set<() => void>();

function read(): UnitSystem {
  try {
    return localStorage.getItem(KEY) === 'imperial' ? 'imperial' : 'metric';
  } catch {
    return 'metric';
  }
}

export function setUnitSystem(system: UnitSystem): void {
  try {
    localStorage.setItem(KEY, system);
  } catch {
    /* private mode — keep the in-memory value only */
  }
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useUnitSystem(): UnitSystem {
  return useSyncExternalStore(subscribe, read, () => 'metric');
}

/**
 * Display/units helper. Weights are always STORED in kilograms; this converts
 * to/from the user's preferred system for input and display. Dimensions keep
 * their own per-item unit; the preference only seeds the default for new items.
 */
export function useUnits() {
  const system = useUnitSystem();
  const { t } = useTranslation();
  const imperial = system === 'imperial';
  const weightLabel = imperial ? t('units.lb') : t('item.kg');

  return {
    system,
    imperial,
    weightLabel,
    defaultDimUnit: (imperial ? 'in' : 'cm') as DimensionUnit,
    /** kg (stored) → number shown in the preferred unit. */
    weightToDisplay: (kg: number) => (imperial ? kgToLb(kg) : kg),
    /** number entered in the preferred unit → kg for storage. */
    weightFromInput: (value: number) => (imperial ? lbToKg(value) : value),
    formatWeight: (kg: number) => `${(imperial ? kgToLb(kg) : kg).toFixed(1)} ${weightLabel}`,
    formatVolume: (m3: number) =>
      imperial ? `${(m3 * M3_TO_FT3).toFixed(1)} ${t('units.ft3')}` : `${m3.toFixed(2)} ${t('units.m3')}`,
  };
}
